import axiosInstance from '../apiServices/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { LESSONS, PROJECT_LESSON_TAGS, LESSON_COMMENTS } from '../apiServices/api_routes';
import { toast } from 'react-toastify';

// Lessons Learnt thunks (CRUD only for now, mirroring basic RAID patterns)

export const GetLessons = createAsyncThunk('GET_LESSONS', async () => {
    const response = await axiosInstance.get(LESSONS + localStorage['project_id'] + '/');
    return response;
});

export const PostLesson = createAsyncThunk('POST_LESSON', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(LESSONS + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchLesson = createAsyncThunk('PATCH_LESSON', async ({ id, body }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(LESSONS + localStorage['project_id'] + '/' + id + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DeleteLesson = createAsyncThunk('DELETE_LESSON', async (id, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(LESSONS + localStorage['project_id'] + '/' + id + '/');
        return { id, response };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const DownloadLessonsTemplate = createAsyncThunk('LESSONS_DOWNLOAD_CSV_TEMPLATE', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(LESSONS + localStorage['project_id'] + '/download_template/', {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Download failed');
    }
});

export const UploadLessonsCSV = createAsyncThunk('LESSONS_UPLOAD_CSV_FILE', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(LESSONS + localStorage['project_id'] + '/bulk_upload_csv/', body, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Upload failed');
    }
});

export const ExportLessonsCSV = createAsyncThunk('EXPORT_LESSONS_CSV', async (lessonIds = null, { rejectWithValue }) => {
    try {
        let url = LESSONS + localStorage['project_id'] + '/export_csv/';
        if (lessonIds && lessonIds.length > 0) {
            // Add lesson_ids as query parameter
            const lessonIdsParam = lessonIds.join(',');
            url += `?lesson_ids=${lessonIdsParam}`;
        }
        const response = await axiosInstance.get(url, {
            responseType: 'blob',
        });
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || 'Export failed');
    }
});

export const BulkUpdateLessons = createAsyncThunk('BULK_UPDATE_LESSONS', async ({ lessonIds, updates }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch(LESSONS + localStorage['project_id'] + '/bulk_update/', {
            lesson_ids: lessonIds,
            updates,
        });
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const BulkDeleteLessons = createAsyncThunk('BULK_DELETE_LESSONS', async ({ lessonIds }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.delete(LESSONS + localStorage['project_id'] + '/bulk_delete/', {
            data: { lesson_ids: lessonIds },
        });
        return { lessonIds, response };
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

// Lesson comments
export const GetLessonComments = createAsyncThunk('GET_LESSON_COMMENTS', async ({ lessonId, projectId }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(LESSON_COMMENTS + lessonId + '/' + projectId + '/');
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PostLessonComment = createAsyncThunk('POST_LESSON_COMMENT', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(LESSON_COMMENTS + body.lesson + '/' + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PatchLessonComment = createAsyncThunk('PATCH_LESSON_COMMENT', async ({ commentId, body }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.patch('/tools/lesson-comments/' + commentId + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const GetLessonsAISummary = createAsyncThunk(
    'GET_LESSONS_AI_SUMMARY',
    async ({ lessonIds, regenerate = false }, { getState, rejectWithValue }) => {
        try {
            const projectId = getState()?.projectData?.selectedProject?.id || localStorage['project_id'];
            if (!projectId || !lessonIds?.length) {
                return rejectWithValue('Project and lesson IDs are required');
            }
            const response = await axiosInstance.post(
                `${LESSONS}${projectId}/ai_summary/`,
                { lesson_ids: lessonIds, regenerate },
            );
            return response.data;
        } catch (error) {
            const message =
                error.response?.data?.error ||
                (typeof error.response?.data === 'string' ? error.response.data : null) ||
                error.message ||
                'Failed to generate Lessons Learned summary';
            return rejectWithValue(message);
        }
    },
);

export const GetProjectLessonTags = createAsyncThunk('GET_PROJECT_LESSON_TAGS', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(PROJECT_LESSON_TAGS + localStorage['project_id'] + '/');
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

export const PostProjectLessonTag = createAsyncThunk('POST_PROJECT_LESSON_TAG', async (body, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post(PROJECT_LESSON_TAGS + localStorage['project_id'] + '/', body);
        return response;
    } catch (error) {
        return rejectWithValue(error.response?.data || error.message);
    }
});

const lessonSlice = createSlice({
    name: 'lessonSlice',
    initialState: {
        lessons: [],
        status: null,
        buttonLoading: false,
        exportCSVLoading: false,
        downloadTemplateLoading: false,
        uploadCSVBtnLoading: false,
        lessonTags: [],
        lessonTagsLoading: false,
        lessonComments: [],
        lessonsSummaryData: null,
        lessonsSummaryLoading: false,
        lessonsSummaryError: null,
    },
    reducers: {
        clearLessonsSummary: state => {
            state.lessonsSummaryData = null;
            state.lessonsSummaryError = null;
        },
        resetLessonData: () => ({
            lessons: [],
            status: null,
            buttonLoading: false,
            exportCSVLoading: false,
            downloadTemplateLoading: false,
            uploadCSVBtnLoading: false,
            lessonTags: [],
            lessonTagsLoading: false,
            lessonComments: [],
            lessonsSummaryData: null,
            lessonsSummaryLoading: false,
            lessonsSummaryError: null,
        }),
    },
    extraReducers: {
        // GetLessons
        [GetLessons.pending]: state => {
            state.status = 'loading';
        },
        [GetLessons.fulfilled]: (state, { payload }) => {
            state.status = 'success';
            state.lessons = payload?.data || [];
        },
        [GetLessons.rejected]: state => {
            state.status = 'rejected';
            toast.error('Failed to load Lessons Learnt');
        },

        // PostLesson
        [PostLesson.pending]: state => {
            state.buttonLoading = true;
        },
        [PostLesson.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            if (payload?.data) {
                state.lessons = state.lessons ? [...state.lessons, payload.data] : [payload.data];
            }
            toast.success('Lesson added');
        },
        [PostLesson.rejected]: (state, action) => {
            state.buttonLoading = false;
            const errors = action.payload;
            if (errors && typeof errors === 'object') {
                Object.keys(errors).forEach(key => {
                    const value = errors[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key.toUpperCase()} : ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else {
                toast.error('Failed to add Lesson');
            }
        },

        // PatchLesson
        [PatchLesson.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.lessons = state.lessons?.map(item => (item.id === payload.data.id ? payload.data : item));
            }
            toast.success('Lesson updated');
        },
        [PatchLesson.rejected]: state => {
            toast.error('Failed to update Lesson');
        },

        // DeleteLesson
        [DeleteLesson.fulfilled]: (state, { payload }) => {
            state.lessons = state.lessons?.filter(item => item.id !== payload?.id);
            toast.success('Lesson deleted');
        },
        [DeleteLesson.rejected]: state => {
            toast.error('Failed to delete Lesson');
        },

        // ExportLessonsCSV
        [ExportLessonsCSV.pending]: state => {
            state.exportCSVLoading = true;
        },
        [ExportLessonsCSV.fulfilled]: (state, action) => {
            state.exportCSVLoading = false;
            const lessonIds = action.meta?.arg;
            const filename = lessonIds && lessonIds.length > 0 ? `lessons_learnt_selected_${lessonIds.length}.csv` : 'lessons_learnt.csv';
            const successMessage =
                lessonIds && lessonIds.length > 0 ? `Successfully exported ${lessonIds.length} lesson${lessonIds.length > 1 ? 's' : ''}` : 'Lessons exported successfully';

            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(successMessage);
        },
        [ExportLessonsCSV.rejected]: (state, action) => {
            state.exportCSVLoading = false;
            toast.error(action.payload || 'Failed to export Lessons');
        },

        // DownloadLessonsTemplate
        [DownloadLessonsTemplate.pending]: state => {
            state.downloadTemplateLoading = true;
        },
        [DownloadLessonsTemplate.fulfilled]: (state, action) => {
            state.downloadTemplateLoading = false;
            const url = window.URL.createObjectURL(new Blob([action.payload.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'lessons_template.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success('Lessons template downloaded successfully');
        },
        [DownloadLessonsTemplate.rejected]: (state, action) => {
            state.downloadTemplateLoading = false;
            toast.error(action.payload || 'Failed to download Lessons template');
        },

        // UploadLessonsCSV
        [UploadLessonsCSV.pending]: state => {
            state.uploadCSVBtnLoading = true;
        },
        [UploadLessonsCSV.fulfilled]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            const createdItems = action.payload?.data?.items || [];
            if (createdItems.length) {
                state.lessons = state.lessons ? [...state.lessons, ...createdItems] : createdItems;
            }
            const created = action.payload?.data?.created ?? createdItems.length;
            const skipped = action.payload?.data?.skipped ?? 0;
            toast.success(`Imported ${created} lesson${created === 1 ? '' : 's'}${skipped ? `, skipped ${skipped}` : ''}`);
        },
        [UploadLessonsCSV.rejected]: (state, action) => {
            state.uploadCSVBtnLoading = false;
            const error = action.payload;
            if (error && typeof error === 'object') {
                Object.keys(error).forEach(key => {
                    const value = error[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key.toUpperCase()} : ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else {
                toast.error(error || 'Failed to import Lessons CSV');
            }
        },

        // BulkUpdateLessons
        [BulkUpdateLessons.pending]: state => {
            state.buttonLoading = true;
        },
        [BulkUpdateLessons.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            const updatedItems = payload?.data?.items || [];
            if (updatedItems.length) {
                const map = new Map(updatedItems.map(item => [item.id, item]));
                state.lessons = state.lessons.map(item => (map.has(item.id) ? map.get(item.id) : item));
            }
            const updated = payload?.data?.updated ?? updatedItems.length;
            toast.success(`Updated ${updated} lesson${updated === 1 ? '' : 's'}`);
        },
        [BulkUpdateLessons.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to bulk update Lessons');
        },

        // BulkDeleteLessons
        [BulkDeleteLessons.pending]: state => {
            state.buttonLoading = true;
        },
        [BulkDeleteLessons.fulfilled]: (state, { payload }) => {
            state.buttonLoading = false;
            const idsToDelete = new Set(payload?.lessonIds || []);
            state.lessons = state.lessons?.filter(item => !idsToDelete.has(item.id));
            const deleted = payload?.lessonIds?.length ?? 0;
            toast.success(`Deleted ${deleted} lesson${deleted === 1 ? '' : 's'}`);
        },
        [BulkDeleteLessons.rejected]: (state, action) => {
            state.buttonLoading = false;
            toast.error(action.payload || 'Failed to bulk delete Lessons');
        },

        // GetProjectLessonTags
        [GetProjectLessonTags.pending]: state => {
            state.lessonTagsLoading = true;
        },
        [GetProjectLessonTags.fulfilled]: (state, { payload }) => {
            state.lessonTagsLoading = false;
            state.lessonTags = payload?.data || [];
        },
        [GetProjectLessonTags.rejected]: (state, action) => {
            state.lessonTagsLoading = false;
            if (action.payload) {
                toast.error('Failed to load lesson tags');
            }
        },

        // PostProjectLessonTag
        [PostProjectLessonTag.pending]: state => {
            state.lessonTagsLoading = true;
        },
        [PostProjectLessonTag.fulfilled]: (state, { payload }) => {
            state.lessonTagsLoading = false;
            if (payload?.data) {
                const existing = state.lessonTags || [];
                const existsByName = existing.some(tag => tag.name === payload.data.name);
                state.lessonTags = existsByName ? existing : [...existing, payload.data];
            }
            toast.success('Tag added');
        },
        [PostProjectLessonTag.rejected]: (state, action) => {
            state.lessonTagsLoading = false;
            const error = action.payload;
            if (error && typeof error === 'object') {
                Object.keys(error).forEach(key => {
                    const value = error[key];
                    if (Array.isArray(value)) {
                        value.forEach(item => toast.error(`${key.toUpperCase()} : ${item}`));
                    } else {
                        toast.error(String(value));
                    }
                });
            } else if (error) {
                toast.error(error);
            } else {
                toast.error('Failed to add tag');
            }
        },

        // GetLessonComments
        [GetLessonComments.fulfilled]: (state, { payload }) => {
            state.lessonComments = payload?.data || [];
        },
        [GetLessonComments.rejected]: (state, action) => {
            toast.error(action.payload || 'Failed to load Lesson comments');
        },

        // PostLessonComment
        [PostLessonComment.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.lessonComments = state.lessonComments ? [...state.lessonComments, payload.data] : [payload.data];
                const lessonId = payload.data.lesson;
                state.lessons = state.lessons.map(item => (item.id === lessonId ? { ...item, comments_count: (item.comments_count || 0) + 1 } : item));
            }
            toast.success('Comment added');
        },
        [PostLessonComment.rejected]: (state, action) => {
            toast.error(action.payload || 'Failed to add comment');
        },

        // PatchLessonComment
        [PatchLessonComment.fulfilled]: (state, { payload }) => {
            if (payload?.data) {
                state.lessonComments = state.lessonComments?.map(item => (item.id === payload.data.id ? payload.data : item));
            }
            toast.success('Comment updated');
        },
        [PatchLessonComment.rejected]: (state, action) => {
            toast.error(action.payload || 'Failed to update comment');
        },

        // GetLessonsAISummary
        [GetLessonsAISummary.pending]: state => {
            state.lessonsSummaryLoading = true;
            state.lessonsSummaryError = null;
        },
        [GetLessonsAISummary.fulfilled]: (state, { payload }) => {
            state.lessonsSummaryLoading = false;
            state.lessonsSummaryData = payload;
            state.lessonsSummaryError = null;
        },
        [GetLessonsAISummary.rejected]: (state, { payload }) => {
            state.lessonsSummaryLoading = false;
            state.lessonsSummaryData = null;
            state.lessonsSummaryError = payload;
        },
    },
});

export const { resetLessonData, clearLessonsSummary } = lessonSlice.actions;
export default lessonSlice.reducer;
